import { ensureCreatedFields } from '@soniq/schema'
import {
  cleanContactRelationshipProperties,
  ContactRelationshipSchema,
  ContactWithId,
} from '@soniq/schema-connection'
import { isNewContactRelationship } from './guards'
import {
  ManageStudentAndTheirContactsSchema,
  NewContactOrExistingRelationship,
} from './types'
import { getAllLogger } from '@pogokid/log'
import { uuidv7 } from '@pogokid/util'

const log = getAllLogger(
  'public-manager-connection:generateContactSubjectAndRelationships'
)

export interface SubjectAndLinksOptions
  extends ManageStudentAndTheirContactsSchema {
  userId: string
}

export function generateContactSubjectAndRelationships({
  userId,
  contacts,
  ...studentData
}: SubjectAndLinksOptions) {
  const subjectId = uuidv7()
  const { newContacts, newContactRelationships, billingContactId } =
    generateSubjectRelationships(userId, subjectId, contacts)

  const student = ensureCreatedFields<ContactWithId>(userId, {
    ...studentData,
    id: subjectId,
    profile_id: userId,
    email: studentData.email ?? null,
    billing_contact_id: billingContactId,
    is_student: true,
    meta: {},
    deleted_at: null,
    deleted_by: null,
    is_archived: false,
  })
  return {
    student,
    newContacts,
    newStudentContacts: newContactRelationships,
  }
}

export function generateSubjectRelationships(
  userId: string,
  subjectId: string,
  contacts: NewContactOrExistingRelationship[]
) {
  const newContacts: ContactWithId[] = []
  const newContactRelationships: ContactRelationshipSchema[] = []
  // The contact selected to receive invoices. Resolved here because a new
  // contact's id is only generated at this point.
  let billingContactId: string | null = null
  for (const newContactOrRelationship of contacts) {
    if (isNewContactRelationship(newContactOrRelationship)) {
      log.info(
        'New contact for relationship',
        newContactOrRelationship
      )
      const contactId = uuidv7()
      if (newContactOrRelationship.is_billing_contact) {
        billingContactId = contactId
      }
      // The new contact and its relationship are built explicitly from
      // named fields, so the UI-only `is_billing_contact` flag is never
      // copied onto either record (no leak-stripping needed here, unlike
      // the existing-contact branch below which spreads the form values).
      newContacts.push(
        ensureCreatedFields<ContactWithId>(userId, {
          id: contactId,
          name: newContactOrRelationship.name,
          email: newContactOrRelationship.email,
          profile_id: userId,
          is_student: false,
          meta: {},
          deleted_at: null,
          deleted_by: null,
          is_archived: false,
        })
      )
      // Relationship to the subject contact
      newContactRelationships.push(
        ensureCreatedFields<ContactRelationshipSchema>(userId, {
          id: uuidv7(),
          profile_id: userId,
          subject_id: subjectId,
          contact_id: contactId,
          relationship:
            newContactOrRelationship.relationship ?? 'unknown',
          deleted_by: null,
          deleted_at: null,
        })
      )
      // Relationship with the current user
      if (userId !== subjectId) {
        newContactRelationships.push(
          ensureCreatedFields<ContactRelationshipSchema>(
            userId,
            {
              id: uuidv7(),
              profile_id: userId,
              subject_id: userId,
              contact_id: contactId,
              relationship: 'unknown',
              deleted_by: null,
              deleted_at: null,
            }
          )
        )
      }
    } else {
      if (newContactOrRelationship.is_billing_contact) {
        billingContactId = newContactOrRelationship.contact_id
      }
      newContactRelationships.push(
        cleanContactRelationshipProperties(
          ensureCreatedFields<ContactRelationshipSchema>(
            userId,
            {
              deleted_at: null,
              deleted_by: null,
              profile_id: userId,
              ...newContactOrRelationship,
              subject_id: subjectId,
              id: newContactOrRelationship.id || uuidv7(),
            }
          )
        )
      )
    }
  }

  return {
    newContactRelationships,
    newContacts,
    billingContactId,
  }
}
